import React, {useEffect, useMemo, useState} from "react";
import axios, {AxiosError} from "axios";
import {ColumnDef} from "@tanstack/react-table";
import {Link, useNavigate, useParams} from "react-router-dom";

import {DataGrid, KeenIcon} from "@/components";
import {Loading} from '@/layouts/buddy/loading';
import {enqueueSnackbar} from "notistack";
import {MatchDetailsData} from "@/pages/buddy/types/MatchDetailsData.ts";
import {getProgressColor} from "@/pages/buddy/utils/ProgressColor.tsx";
import {formatDate} from "@/pages/buddy/utils/Date.tsx";

interface MatchData {
    p_id: string;
    job_p_id: string;

    job_name: string;
    job_description: string;
    job_location: string;
    job_employment_type: string;
    job_salary_range: string;
    job_required_skills: string[];
    job_role_responsibilities: string[];

    company_name: string;

    matched_at: string | null;
    hard_criteria_passed: boolean;
    overall_soft_score_relative: number;

    hard_criteria_results: Record<string, boolean>;
    soft_criteria_details: Record<string, { match_score: number; explanation: string }>;
}

const Matches = () => {
    const {p_id: candidate_p_id} = useParams();
    const [data, setData] = useState<MatchData[]>([]);
    const [loading, setLoading] = useState(true);

    const [isActiveFilter, setIsActiveFilter] = useState(() => {
        const stored = localStorage.getItem("matches-active-filter");
        return stored ? stored === "true" : true;
    });
    const [hasToggledFilter, setHasToggledFilter] = useState(() => {
        return localStorage.getItem("matches-active-filter") !== null;
    });

    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem("matches-filter") || "";
    });

    const API_MATCHES = `${import.meta.env.VITE_APP_API_URL}/matches`;
    const API_MATCH = `${import.meta.env.VITE_APP_API_URL}/match`;

    const navigate = useNavigate();

    const fetchMatchesData = async () => {
        try {
            const response = await axios.get<MatchData[]>(`${API_MATCHES}/${candidate_p_id}`, {
                headers: {Accept: "application/json"},
            });
            setData(response.data);
            setLoading(false);
        } catch (err) {
            enqueueSnackbar(`Failed to fetch matches data: ${err}`, {
                variant: 'solid',
                state: 'danger'
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMatchesData();
    }, []);

    useEffect(() => {
        localStorage.setItem("matches-filter", searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        localStorage.setItem("matches-active-filter", String(isActiveFilter));
    }, [isActiveFilter]);

    const generateMatch = async (job_p_id: string, candidate_p_id: string) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.post(
                `${API_MATCH}/${job_p_id}/${candidate_p_id}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                navigate(`/candidate/${candidate_p_id}`);
            }
        } catch (err) {
            if (err instanceof AxiosError && err.response) {
                enqueueSnackbar(`Failed to generate match: ${err}`, {
                    variant: 'solid',
                    state: 'danger'
                });
            } else if (err instanceof Error) {
                enqueueSnackbar(`Error: ${err}`, {
                    variant: 'solid',
                    state: 'danger'
                });
            } else {
                enqueueSnackbar(`An unknown error occurred: ${err}`, {
                    variant: 'solid',
                    state: 'danger'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo<ColumnDef<MatchDetailsData>[]>(
        () => [
            // Job column
            {
                accessorFn: (row) => row.job_name,
                id: 'job',
                header: () => 'Job',
                enableSorting: true,
                cell: (info) => (
                    <div className="flex flex-col">
                        <Link
                            className="leading-none font-normal text-sm text-gray-700 hover:text-primary"
                            to={`/job/${info.row.original.job_p_id}`}
                        >
                            {info.row.original.job_name}
                        </Link>
                    </div>
                ),
            },
            // Score column with progress bar
            {
                accessorFn: (row) => row.overall_soft_score_relative,
                id: 'score',
                header: () => 'Match',
                enableSorting: true,
                cell: ({row, getValue}) => {
                    const value = getValue() as number | null;

                    if (value === null || value === undefined) {
                        return 'No score';
                    }

                    const percentage = Math.round(value * 100);

                    return (
                        <div className="flex items-center gap-2">
                            <>
                                <button
                                    className="btn btn-sm btn-icon btn-clear btn-light"
                                    onClick={() => navigate(`/match/${row.original.p_id}`)}
                                >
                                    <KeenIcon icon="eye"/>
                                </button>
                                <Link
                                    className="leading-none hover:text-primary"
                                    to={`/match/${row.original.p_id}`}
                                >
                                    {percentage}%
                                </Link>
                            </>
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div
                                    className="h-1.5 rounded-full"
                                    style={{width: `${percentage}%`, backgroundColor: getProgressColor(percentage)}}
                                ></div>
                            </div>
                        </div>
                    );
                },
                meta: {
                    className: 'w-[250px]',
                    cellClassName: 'text-gray-700 font-normal',
                },
            },
            // Last Modified column
            {
                accessorFn: (row) => row.updated_at,
                id: 'lastModified',
                enableSorting: true,
                header: () => 'Last Modified',
                cell: (info) => {
                    const value = info.getValue() as string | null;
                    return formatDate(value);
                },
                meta: {
                    className: 'w-[350px]',
                    cellClassName: 'text-gray-700 font-normal',
                },
            },
            // Open column
            {
                id: 'edit',
                header: () => 'Open',
                enableSorting: false,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => navigate(`/match/${row.original.p_id}`)}
                    >
                        <KeenIcon icon="notepad-edit"/>
                    </button>
                ),
                meta: {
                    className: 'w-[60px]',
                },
            },
            // Delete column
            /*{
                id: 'delete',
                header: () => 'Delete',
                enableSorting: false,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => handleDeleteClick(row.original)}
                    >
                        <KeenIcon icon="trash"/>
                    </button>
                ),
                meta: {
                    className: 'w-[60px]',
                },
            },*/
        ],
        [navigate]
    );

    const filteredData = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();

        return data.filter((match) => {
            const matchesSearch =
                (match.job_name?.toLowerCase() || "").includes(searchLower) ||
                (match.job_description?.toLowerCase() || "").includes(searchLower);

            const matchesActiveFilter = !isActiveFilter || !hasToggledFilter || match.overall_soft_score_relative > 0.0;

            return matchesSearch && matchesActiveFilter;
        });
    }, [searchTerm, isActiveFilter, hasToggledFilter, data]);

    if (loading) return <Loading loading={loading}/>;

    return (
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">Matches</h3>
                <div className="flex gap-6">
                    <div className="relative">
                        <KeenIcon
                            icon="magnifier"
                            className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                        />
                        <input
                            type="text"
                            placeholder="Search Matches"
                            className="input input-sm pl-8"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <label className="switch switch-sm">
                        <input
                            name="check"
                            type="checkbox"
                            checked={isActiveFilter}
                            onChange={() => {
                                setIsActiveFilter(!isActiveFilter);
                                setHasToggledFilter(true);
                            }}
                            className="order-2"
                        />
                        <span className="switch-label order-1">High Match Score Only</span>
                    </label>
                </div>
            </div>

            <div className="card-body">
                <DataGrid
                    key={`${searchTerm}-${isActiveFilter}`}
                    columns={columns}
                    data={filteredData}
                    pagination={{size: 10}}
                    sorting={[{id: "job", desc: false}]}
                />
            </div>
        </div>
    );
};

export {Matches};
